import { createClient, type Client } from '@connectrpc/connect'
import { BuildingService } from '@/gen/building/v1/building_pb'
import { NpcService } from '@/gen/npc/v1/npc_pb'
import { PlayerService } from '@/gen/player/v1/player_pb'
import { PoliceService } from '@/gen/police/v1/police_pb'
import { RoamerService } from '@/gen/roamer/v1/roamer_pb'
import { CrewMapService } from '@/gen/webgame/v1/crew_map_pb'
import { WebSessionService } from '@/gen/webgame/v1/session_pb'
import { WorldMapService } from '@/gen/webgame/v1/world_map_pb'
import { getTransport, transportGeneration } from './transports'

/**
 * Typed clients, each bound to the host that serves it.
 *
 * THE WEB TOKEN IS SCOPED to a read-only allowlist server-side:
 *   webgame.v1.*, PlayerService/GetPlayer, BuildingService/{GetBuilding,
 *   ListPlayerBuildings}, NpcService/{ListNearbyNPCs,GetNPC},
 *   PoliceService/{ListNpcsNear,GetPublicConfig},
 *   RoamerService/{ListRoamersNear,GetPublicConfig}.
 *
 * NEVER call PlayerService.LoginWithGoogle (creates players),
 * PlayerService.PlayerLogout (revokes the PHONE's location token),
 * PlayerService.RefreshToken (refuses web tokens), or
 * BuildingService.ListNearbyBuildings (paid POI ingestion). The ESLint config
 * fails the build on a call to any of them.
 */
export interface Clients {
  webSession: Client<typeof WebSessionService>
  crewMap: Client<typeof CrewMapService>
  player: Client<typeof PlayerService>
  worldMap: Client<typeof WorldMapService>
  building: Client<typeof BuildingService>
  npc: Client<typeof NpcService>
  police: Client<typeof PoliceService>
  roamer: Client<typeof RoamerService>
}

let cache: { gen: number; clients: Clients } | null = null

export function api(): Clients {
  const gen = transportGeneration()
  if (!cache || cache.gen !== gen) {
    const auth = getTransport('auth')
    const building = getTransport('building')
    const npc = getTransport('npc')
    cache = {
      gen,
      clients: {
        webSession: createClient(WebSessionService, auth),
        crewMap: createClient(CrewMapService, auth),
        player: createClient(PlayerService, auth),
        worldMap: createClient(WorldMapService, building),
        building: createClient(BuildingService, building),
        npc: createClient(NpcService, building),
        police: createClient(PoliceService, npc),
        roamer: createClient(RoamerService, npc),
      },
    }
  }
  return cache.clients
}
